import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  Chip,
  LinearProgress,
  InputAdornment,
  Popper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useDispatch, useSelector } from "react-redux";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmIwoList, insertPmFrame, fetchPmFrameList, deletePmFrame } from "../actions/ProductionMonitorAction";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditFrameDialog from "./Editframedialog";

/* -------------------- Constants -------------------- */
// value = what is sent to the API, label = what the user sees (no "_")
const COMPONENTS = [
  { value: "BHD", label: "BHD" },
  { value: "SHELL_GIRDER", label: "Shell Girder" },
  { value: "DECK_GIRDER", label: "Deck Girder" },
  { value: "DECK_01", label: "Deck 01" },
  { value: "DECK_02", label: "Deck 02" },
  { value: "DECK_03", label: "Deck 03" },
  { value: "DECK_04", label: "Deck 04" },
  { value: "DECK_05", label: "Deck 05" },
  { value: "DECK_06", label: "Deck 06" },
  { value: "DECK_07", label: "Deck 07" },
];

// Editable columns in Tab order
const COLS = ["component", "frameNo", "startDate", "endDate", "weight", "partNos"];

// Weight: numbers only, up to 3 decimals ("1250.5")
const cleanWeight = (text) => {
  const t = String(text || "").replace(/[^\d.]/g, "");
  const [whole, ...rest] = t.split(".");
  return rest.length ? `${whole}.${rest.join("").slice(0, 3)}` : whole;
};
const isValidWeight = (text) => /^\d+(\.\d+)?$/.test(String(text || "")) && Number(text) > 0;

// Weight for display: 1250.5 -> "1,250.5 kg", null -> "-"
const showWeight = (w) =>
  w === null || w === undefined || w === "" ? "-" : `${Number(w).toLocaleString("en-US", { maximumFractionDigits: 3 })} kg`;

const NAVY = "#142c54";
const BLUE = "#154c79";

// Local yyyy-MM-dd 
const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Typed digits -> yyyy-MM-dd as you type
const formatDateTyping = (raw) => {
  const d = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`;
};

// true only for a real calendar date
const isValidDateText = (t) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t || "")) return false;
  const [y, m, d] = t.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

// Part No Array
const splitParts = (text) =>
  Array.from(
    new Set(
      String(text || "")
        .split(/[\s,;]+/)
        .map((p) => p.trim())
        .filter(Boolean)
    )
  );

// Frame / OCL No rules (same code is in InsertFrames.js and Editframedialog.js)
//   Frame -24 Port Side | Frame -24 Starboard Side | Frame -24 Center | Frame 12
//   OCL 700 Port Side   | OCL 700 Starboard Side   | OCL 700 Center   | OCL 05

const FRAME_SIDES = [
  { key: "p", label: "Port Side" },
  { key: "s", label: "Starboard Side" },
  { key: "c", label: "Center" },
];

const FRAME_NO_HINT = "Use Frame -24 / OCL 700 + Port Side / Starboard Side / Center";

const SIDE_RE = "(Port Side|Starboard Side|Center)";
const PREFIXES = ["frame", "ocl"];

// frame / FRAME -> Frame, ocl / Ocl -> OCL
const fixFrameNo = (text) =>
  String(text || "")
    .replace(/\bframe(?![a-z])/gi, "Frame")
    .replace(/\bocl(?![a-z])/gi, "OCL");

// While typing: can this text still become a valid value?
const canTypeFrameNo = (text) => {
  const t = String(text || "");
  const lower = t.toLowerCase();
  if (!t) return true;
  if (/^\s/.test(t)) return false; // no leading space
  if (PREFIXES.some((p) => p.startsWith(lower))) return true; // "F", "Fra", "OC"

  const prefix = PREFIXES.find((p) => lower.startsWith(p));
  if (!prefix) return false;

  const rest = lower.slice(prefix.length);
  if (/^ ?-? ?\d*$/.test(rest)) return true; // "Frame -24", "OCL 700"
  const m = /^ ?-? ?\d+ (.*)$/.exec(rest); // "OCL 700 Port Si"
  if (!m) return false;
  return FRAME_SIDES.some((s) => s.label.toLowerCase().startsWith(m[1]));
};

// "frame-24 port side" -> "Frame -24 Port Side", "ocl700 c" -> "OCL 700 Center", "ocl   5" -> "OCL 5"
const normalizeFrameNo = (text) => {
  const t = fixFrameNo(text).trim().replace(/\s+/g, " ");
  const f = new RegExp(`^(Frame|OCL) ?(-)? ?(\\d+)(?: ${SIDE_RE})?$`, "i").exec(t);
  if (!f) return t;
  const side = f[4] ? FRAME_SIDES.find((s) => s.label.toLowerCase() === f[4].toLowerCase()).label : "";
  return `${f[1]} ${f[2] || ""}${f[3]}${side ? ` ${side}` : ""}`;
};

const isValidFrameNo = (text) => new RegExp(`^(Frame|OCL) -?\\d+( ${SIDE_RE})?$`).test(normalizeFrameNo(text));

// "Frame -24" / "OCL 700" typed, side not chosen yet -> show Port / Starboard / Center suggestions
const needsFrameSide = (text) => /^(Frame|OCL) ?-? ?\d+ ?$/.test(fixFrameNo(text));

// "OCL 700" + "Port Side" -> "OCL 700 Port Side"
const withFrameSide = (text, side) => `${normalizeFrameNo(text)} ${side}`;

// Handle one change in the box. Returns the new value, or null to ignore the key.
//  - p / s / c after the number -> Port Side / Starboard Side / Center (Frame and OCL)
//  - backspace inside a side -> removes the whole side
//  - only Frame.. / OCL.. can be typed
const applyFrameNoTyping = (prev, raw) => {
  const before = String(prev || "");
  const v = fixFrameNo(raw);

  if (v.length < before.length) {
    const full = new RegExp(`^((?:Frame|OCL) ?-? ?\\d+) ${SIDE_RE}$`).exec(before);
    if (full && v.startsWith(full[1]) && v.length > full[1].length) return `${full[1]} `;
    return v; // deleting is always allowed
  }

  const short = /^((?:Frame|OCL) ?-? ?\d+) ?([psc])$/i.exec(v);
  if (short) {
    const side = FRAME_SIDES.find((s) => s.key === short[2].toLowerCase()).label;
    return withFrameSide(short[1], side);
  }

  return canTypeFrameNo(v) ? v : null;
};

let rowSeq = 0;
const newRow = (component = "BHD") => ({
  id: ++rowSeq,
  component,
  frameNo: "",
  startDate: "",
  endDate: "",
  weight: "",
  partNos: "",
  status: "", 
  message: "",
});

const isRowEmpty = (r) => !r.frameNo.trim() && !r.startDate && !r.endDate && !r.weight && !r.partNos.trim();

// Returns { field: errorText } for a row (empty object = valid)
const rowErrors = (r) => {
  const e = {};
  if (!r.component) e.component = "Required";
  if (!r.frameNo.trim()) e.frameNo = "Required";
  else if (!isValidFrameNo(r.frameNo)) e.frameNo = FRAME_NO_HINT;
  if (!isValidDateText(r.startDate)) e.startDate = r.startDate ? "Invalid date" : "Required";
  if (!isValidDateText(r.endDate)) e.endDate = r.endDate ? "Invalid date" : "Required";
  if (!e.startDate && !e.endDate && r.startDate > r.endDate) e.endDate = "Before start";
  if (!r.weight) e.weight = "Required";
  else if (!isValidWeight(r.weight)) e.weight = "Invalid weight";
  if (splitParts(r.partNos).length === 0) e.partNos = "Required";
  return e;
};

/* -------------------- Small UI pieces -------------------- */
const cellInputSx = (error, locked) => ({
  width: "100%",
  boxSizing: "border-box",
  height: 38,
  px: 1.25,
  border: "1px solid",
  borderColor: error ? "#e57373" : "transparent",
  borderRadius: "8px",
  backgroundColor: locked ? "transparent" : error ? "#fff5f5" : "#f7f9fc",
  fontFamily: "inherit",
  fontSize: "14px",
  color: "#1f2937",
  outline: "none",
  transition: "border-color 0.1s ease, background-color 0.1s ease",
  "&:focus": {
    borderColor: BLUE,
    backgroundColor: "#fff",
    boxShadow: "0 0 0 3px rgba(21,76,121,0.15)",
  },
  "&:disabled": { color: "#4b5563", cursor: "default" },
  "&::placeholder": { color: "#a0a8b5" },
});

const ErrText = ({ children }) =>
  children ? <Typography sx={{ fontSize: "11px", color: "#c62828", mt: 0.25, ml: 0.5 }}>{children}</Typography> : null;

/* -------------------- Inserted Frames (from GetFrameDetails) -------------------- */
// Display name for a component value: SHELL_GIRDER -> Shell Girder (unknown values: "_" -> space)
const COMPONENT_LABEL = COMPONENTS.reduce((m, c) => ({ ...m, [c.value]: c.label }), { DECK: "Deck" });
const componentLabel = (v) =>
  COMPONENT_LABEL[v] ||
  String(v || "")
    .split("_")
    .map((w) => (w.length > 3 ? w.charAt(0) + w.slice(1).toLowerCase() : w))
    .join(" ");

const PartsChips = ({ parts, max = 6 }) => {
  const list = (parts || []).map((p) => p.PartNo);
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {shown.map((p, i) => (
        <Chip key={`${p}-${i}`} label={p} size="small" sx={{ height: 22, fontSize: "11px", backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
      ))}
      {rest > 0 && (
        <Tooltip title={list.slice(max).join(", ")}>
          <Chip label={`+${rest}`} size="small" sx={{ height: 22, fontSize: "11px", fontWeight: 700 }} />
        </Tooltip>
      )}
    </Box>
  );
};

const Progress = ({ value, done }) => {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Box sx={{ minWidth: 90 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
        <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{done ? "Completed" : "In progress"}</Typography>
        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#0E3C66" }}>{v}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={done ? 100 : v}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "#e8edf3",
          "& .MuiLinearProgress-bar": { backgroundColor: done ? "#2e7d32" : BLUE },
        }}
      />
    </Box>
  );
};

const InsertedFrames = ({ iwo, isMobile }) => {
  const dispatch = useDispatch();
  const { frameList, frameListLoading, frameListError, iwoList, frameDeleteLoading } = useSelector(
    (state) => state.productionMonitor
  );
  const [search, setSearch] = useState("");
  const [editFrame, setEditFrame] = useState(null);
  const [editSnack, setEditSnack] = useState("");
  const [deleteFrame, setDeleteFrame] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  const handleDelete = async () => {
    if (!deleteFrame) return;
    setDeleteError("");
    const result = await dispatch(
      deletePmFrame({ frameId: String(deleteFrame.FrameId), deletedBy: localStorage.getItem("employeeId") || "" })
    );
    if (result.success) {
      setEditSnack(`${deleteFrame.FrameNo} deleted`);
      setDeleteFrame(null);
      dispatch(fetchPmFrameList()); // reload from server
    } else {
      setDeleteError(result.message); // backend message
    }
  };

  const handleEdited = (message) => {
    setEditFrame(null);
    setEditSnack(message);
    dispatch(fetchPmFrameList()); // reload list with the new values
  };

  const frames = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (frameList || [])
      .filter((f) => !iwo || String(f.IwoNo) === String(iwo.IwoNo))
      .filter(
        (f) =>
          !q ||
          [f.IwoNo, f.FrameNo, f.ComponentName, componentLabel(f.ComponentName), ...(f.Parts || []).map((p) => p.PartNo)].some((v) =>
            String(v || "").toLowerCase().includes(q)
          )
      )
      .sort((a, b) => String(b.CreatedDate || "").localeCompare(String(a.CreatedDate || "")));
  }, [frameList, iwo, search]);

  const totalParts = frames.reduce((n, f) => n + (f.Parts || []).length, 0);
  const showIwoCol = !iwo;

  const th = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#eef2f7",
    color: "#0E3C66",
    fontWeight: 700,
    fontSize: "12px",
    textAlign: "left",
    padding: "10px 8px",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #d6dce5",
  };
  const td = { padding: "10px 8px", borderBottom: "1px solid #eef1f5", fontSize: "13px", verticalAlign: "middle" };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        p: { xs: 1.5, md: 2 },
        borderRadius: "16px",
        border: "1px solid #d6dce5",
        boxShadow: "0px 6px 15px rgba(0,0,0,0.06)",
        backgroundColor: "#fff",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, color: "#0E3C66", fontSize: "16px" }}>Inserted Frames</Typography>
        <Chip
          size="small"
          label={iwo ? `IWO ${iwo.IwoNo}` : "All IWOs"}
          sx={{ backgroundColor: iwo ? NAVY : "#eef2f7", color: iwo ? "#fff" : "#0E3C66", fontWeight: 600 }}
        />
        <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
          {frames.length} frame(s) · {totalParts} part(s)
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1, width: { xs: "100%", sm: "auto" } }}>
          <TextField
            size="small"
            placeholder="Search frame, component, part no"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: { sm: 260 }, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => dispatch(fetchPmFrameList())} disabled={frameListLoading} tabIndex={-1}>
                {frameListLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {frameListError && <Alert severity="error" sx={{ mb: 1, borderRadius: "10px" }}>{frameListError}</Alert>}

      {!frameListLoading && !frameListError && frames.length === 0 && (
        <Typography sx={{ textAlign: "center", color: "#9aa3b2", fontSize: "13px", py: 4 }}>
          {search ? "No frames match your search" : iwo ? `No frames inserted yet for IWO ${iwo.IwoNo}` : "No frames inserted yet"}
        </Typography>
      )}

      {/* Mobile: cards */}
      {isMobile && frames.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {frames.map((f) => (
            <Box key={f.FrameId} sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #eef1f5", backgroundColor: "#fafbfd" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontWeight: 700, color: "#0E3C66", fontSize: "14px" }}>{f.FrameNo}</Typography>
                <Chip label={componentLabel(f.ComponentName)} size="small" sx={{ height: 22, fontSize: "11px" }} />
                {showIwoCol && <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>IWO {f.IwoNo}</Typography>}
                <IconButton size="small" onClick={() => setEditFrame(f)} aria-label={`Edit ${f.FrameNo}`} sx={{ ml: "auto", color: BLUE }}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => setDeleteFrame(f)} aria-label={`Delete ${f.FrameNo}`} sx={{ color: "#c62828" }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: "12px", color: "#555", mt: 0.5 }}>
                {f.StartDate} → {f.EndDate} · {showWeight(f.Weight)}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <PartsChips parts={f.Parts} max={8} />
              </Box>
              <Box sx={{ mt: 1.25 }}>
                <Progress value={f.PhysicalCompletion} done={f.CompletionStatus} />
              </Box>
              <Typography sx={{ fontSize: "11px", color: "#9aa3b2", mt: 0.75 }}>
                {(f.DailyRemarks || []).length} remark(s) · By {f.CreatedBy} · {f.CreatedDate}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Desktop: table */}
      {!isMobile && frames.length > 0 && (
        <Box sx={{ overflow: "auto", maxHeight: "50vh", borderRadius: "12px", border: "1px solid #eef1f5" }}>
          <Box component="table" sx={{ width: "100%", minWidth: 900, borderCollapse: "separate", borderSpacing: 0 }}>
            <Box component="thead">
              <Box component="tr">
                {showIwoCol && <Box component="th" sx={th}>IWO</Box>}
                <Box component="th" sx={th}>Frame / OCL No</Box>
                <Box component="th" sx={th}>Component</Box>
                <Box component="th" sx={th}>Start Date</Box>
                <Box component="th" sx={th}>End Date</Box>
                <Box component="th" sx={{ ...th, textAlign: "right" }}>Weight</Box>
                <Box component="th" sx={{ ...th, width: "28%" }}>Part Nos</Box>
                <Box component="th" sx={{ ...th, width: 140 }}>Progress</Box>
                <Box component="th" sx={{ ...th, textAlign: "center" }}>Remarks</Box>
                <Box component="th" sx={th}>Created</Box>
                <Box component="th" sx={{ ...th, textAlign: "center", width: 88 }}>Actions</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {frames.map((f, i) => (
                <Box component="tr" key={f.FrameId} sx={{ backgroundColor: i % 2 ? "#fbfcfe" : "#fff", "&:hover": { backgroundColor: "#eef4fb" } }}>
                  {showIwoCol && <Box component="td" sx={{ ...td, fontWeight: 600 }}>{f.IwoNo}</Box>}
                  <Box component="td" sx={{ ...td, fontWeight: 700, color: "#0E3C66", whiteSpace: "nowrap" }}>{f.FrameNo}</Box>
                  <Box component="td" sx={td}>{componentLabel(f.ComponentName)}</Box>
                  <Box component="td" sx={{ ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{f.StartDate}</Box>
                  <Box component="td" sx={{ ...td, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{f.EndDate}</Box>
                  <Box component="td" sx={{ ...td, whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums", color: f.Weight == null ? "#9aa3b2" : "inherit" }}>
                    {showWeight(f.Weight)}
                  </Box>
                  <Box component="td" sx={td}><PartsChips parts={f.Parts} /></Box>
                  <Box component="td" sx={td}><Progress value={f.PhysicalCompletion} done={f.CompletionStatus} /></Box>
                  <Box component="td" sx={{ ...td, textAlign: "center" }}>{(f.DailyRemarks || []).length}</Box>
                  <Box component="td" sx={{ ...td, whiteSpace: "nowrap" }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{f.CreatedBy}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#9aa3b2" }}>{f.CreatedDate}</Typography>
                  </Box>
                  <Box component="td" sx={{ ...td, textAlign: "center", whiteSpace: "nowrap" }}>
                    <Tooltip title="Edit frame">
                      <IconButton size="small" onClick={() => setEditFrame(f)} aria-label={`Edit ${f.FrameNo}`} tabIndex={-1} sx={{ color: BLUE }}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete frame">
                      <IconButton size="small" onClick={() => setDeleteFrame(f)} aria-label={`Delete ${f.FrameNo}`} tabIndex={-1} sx={{ color: "#c62828" }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
      <EditFrameDialog
        open={!!editFrame}
        frame={editFrame}
        iwoList={iwoList}
        components={COMPONENTS}
        onClose={() => setEditFrame(null)}
        onSaved={handleEdited}
      />

      {/* Delete confirm */}
      <Dialog
        open={!!deleteFrame}
        onClose={frameDeleteLoading ? undefined : () => { setDeleteFrame(null); setDeleteError(""); }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ color: "#c62828", fontWeight: 700 }}>Delete frame?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ fontSize: "14px", color: "#333" }}>
            Delete <b>{deleteFrame?.FrameNo}</b> ({componentLabel(deleteFrame?.ComponentName)}) from IWO <b>{deleteFrame?.IwoNo}</b>?
            <Box sx={{ mt: 1, fontSize: "13px", color: "#6b7280" }}>
              {(deleteFrame?.Parts || []).length} part(s)
              {(deleteFrame?.DailyRemarks || []).length > 0 && ` · ${(deleteFrame?.DailyRemarks || []).length} daily remark(s)`}
            </Box>
            <Box sx={{ mt: 1, fontSize: "12px", color: "#c62828" }}>This cannot be undone.</Box>
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: "10px" }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDeleteFrame(null); setDeleteError(""); }} disabled={frameDeleteLoading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={frameDeleteLoading}
            startIcon={frameDeleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!editSnack}
        autoHideDuration={3000}
        onClose={() => setEditSnack("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setEditSnack("")} sx={{ width: "100%" }}>
          {editSnack}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

/* -------------------- Insert Frames (table) -------------------- */
const InsertFramesTable = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // phones + small tablets -> cards
  const { iwoList, iwoListLoading, iwoListError } = useSelector((state) => state.productionMonitor);

  const [iwo, setIwo] = useState(null);
  const [rows, setRows] = useState(() => [newRow()]);
  const [frameFocusId, setFrameFocusId] = useState(null); // row whose Frame No box has focus (for side suggestions)
  const [showErrors, setShowErrors] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const iwoRef = useRef(null);
  const cellRefs = useRef({}); // key: `${rowId}-${col}`
  const pendingFocus = useRef(null);

  const createdBy = localStorage.getItem("employeeId") || "";
  const iwoOptions = useMemo(() => iwoList || [], [iwoList]);

  useEffect(() => {
    dispatch(fetchPmIwoList());
    dispatch(fetchPmFrameList()); // inserted frames list
    setTimeout(() => iwoRef.current && iwoRef.current.focus(), 0);
  }, [dispatch]);

  // focus a cell after rows re-render (e.g. a new row was added)
  useEffect(() => {
    if (pendingFocus.current) {
      const el = cellRefs.current[pendingFocus.current];
      pendingFocus.current = null;
      if (el) {
        el.focus();
        if (el.select) el.select();
      }
    }
  });

  const focusCell = (rowId, col) => {
    const el = cellRefs.current[`${rowId}-${col}`];
    if (el) {
      el.focus();
      if (el.select) el.select();
    }
  };

  /* ---- Row editing ---- */
  const editableRows = rows.filter((r) => r.status !== "saved");

  const updateRow = (id, field, value) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, [field]: value, ...(r.status === "error" ? { status: "", message: "" } : {}) }
          : r
      )
    );

  const addRowAfter = (afterId) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === afterId);
      const base = prev[idx] || prev[prev.length - 1];
      const row = newRow(base ? base.component : "BHD");
      pendingFocus.current = `${row.id}-frameNo`;
      const next = [...prev];
      next.splice(idx === -1 ? next.length : idx + 1, 0, row);
      return next;
    });
  };

  const removeRow = (id) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === id);
      const next = prev.filter((r) => r.id !== id);
      if (next.filter((r) => r.status !== "saved").length === 0) {
        const row = newRow(prev[idx]?.component || "BHD");
        pendingFocus.current = `${row.id}-frameNo`;
        return [...next, row];
      }
      const target = next[Math.min(idx, next.length - 1)];
      if (target && target.status !== "saved") pendingFocus.current = `${target.id}-frameNo`;
      return next;
    });
  };

  /* ---- Validation ---- */
  const filledRows = editableRows.filter((r) => !isRowEmpty(r));
  const invalidRows = filledRows.filter((r) => Object.keys(rowErrors(r)).length > 0);
  const readyRows = filledRows.filter((r) => Object.keys(rowErrors(r)).length === 0);
  const savedCount = rows.filter((r) => r.status === "saved").length;

  /* ---- Save ---- */
  const requestSave = useCallback(() => {
    if (saving) return;
    if (!iwo) {
      setSnack({ open: true, severity: "warning", message: "Select an IWO first" });
      setTimeout(() => iwoRef.current && iwoRef.current.focus(), 0);
      return;
    }
    if (filledRows.length === 0) {
      setSnack({ open: true, severity: "warning", message: "Enter at least one frame" });
      return;
    }
    if (invalidRows.length > 0) {
      setShowErrors(true);
      const first = invalidRows[0];
      const firstBad = COLS.find((c) => rowErrors(first)[c]);
      focusCell(first.id, firstBad);
      setSnack({
        open: true,
        severity: "error",
        message: `Fix ${invalidRows.length} row(s) before saving (marked in red)`,
      });
      return;
    }
    setConfirmOpen(true);
  }, [saving, iwo, filledRows, invalidRows]);

  const handleSave = async () => {
    setConfirmOpen(false);
    setSaving(true);
    let ok = 0;
    let fail = 0;
    let firstFailId = null;
    let firstFailMessage = "";

    // one InsertFrame call per row, in order
    for (const r of readyRows) {
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, status: "saving", message: "" } : x)));
      const payload = {
        iwoNo: String(iwo.IwoNo),
        componentName: r.component,
        frameNo: normalizeFrameNo(r.frameNo),
        startDate: r.startDate,
        endDate: r.endDate,
        weight: Number(r.weight),
        createdBy,
        partNos: splitParts(r.partNos),
      };
      // eslint-disable-next-line no-await-in-loop
      const result = await dispatch(insertPmFrame(payload));
      if (result.success) ok += 1;
      else {
        fail += 1;
        if (firstFailId === null) {
          firstFailId = r.id;
          firstFailMessage = result.message;
        }
      }
      setRows((prev) =>
        prev.map((x) =>
          x.id === r.id
            ? { ...x, status: result.success ? "saved" : "error", message: result.success ? "" : result.message }
            : x
        )
      );
    }

    setSaving(false);
    setShowErrors(false);
    if (ok > 0) dispatch(fetchPmFrameList()); // show newly inserted frames

    // keep a blank row ready for the next entry
    setRows((prev) => {
      // a row failed -> put the cursor on it to fix and save again
      if (firstFailId !== null) pendingFocus.current = `${firstFailId}-frameNo`;
      const remaining = prev.filter((x) => x.status !== "saved");
      const lastComp = prev[prev.length - 1]?.component || "BHD";
      if (remaining.every((x) => !isRowEmpty(x))) {
        const row = newRow(lastComp);
        if (fail === 0) pendingFocus.current = `${row.id}-frameNo`;
        return [...prev, row];
      }
      const blank = remaining.find((x) => isRowEmpty(x));
      if (fail === 0 && blank) pendingFocus.current = `${blank.id}-frameNo`;
      return prev;
    });

    setSnack({
      open: true,
      severity: fail === 0 ? "success" : ok > 0 ? "warning" : "error",
      message:
        fail === 0
          ? `${ok} frame(s) saved for IWO ${iwo.IwoNo}`
          : fail === 1
          ? `${ok > 0 ? `${ok} saved, 1 failed: ` : ""}${firstFailMessage || "Failed to save"}` // backend message (e.g. Part No already exists)
          : `${ok} saved, ${fail} failed — check the red rows`,
    });
  };

  const handleNewBatch = () => {
    const row = newRow();
    setRows([row]);
    setShowErrors(false);
    pendingFocus.current = `${row.id}-frameNo`;
  };

  // Ctrl+S / Ctrl+Enter -> Save
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S" || e.key === "Enter")) {
        e.preventDefault();
        if (!confirmOpen) requestSave();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [requestSave, confirmOpen]);

  /* ---- Cell keyboard ---- */
  const handleCellKeyDown = (e, row, col) => {
    const rowIdx = editableRows.findIndex((r) => r.id === row.id);
    const colIdx = COLS.indexOf(col);

    // Tab on the last cell of the last row -> new row
    if (e.key === "Tab" && !e.shiftKey && colIdx === COLS.length - 1 && rowIdx === editableRows.length - 1) {
      e.preventDefault();
      addRowAfter(row.id);
      return;
    }
    // Enter -> same column, next row (adds a row at the bottom)
    if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      const next = editableRows[rowIdx + 1];
      if (next) focusCell(next.id, col);
      else addRowAfter(row.id);
      return;
    }
    // Arrow up / down -> move between rows (not on the component select)
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && col !== "component") {
      const target = editableRows[rowIdx + (e.key === "ArrowDown" ? 1 : -1)];
      if (target) {
        e.preventDefault();
        focusCell(target.id, col);
      }
      return;
    }
    // Ctrl + Delete -> remove row
    if (e.key === "Delete" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      removeRow(row.id);
      return;
    }
    // Dates: "t" = today
    if ((col === "startDate" || col === "endDate") && e.key.toLowerCase() === "t" && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      updateRow(row.id, col, formatDate(new Date()));
    }
  };

  const setRef = (rowId, col) => (el) => {
    if (el) cellRefs.current[`${rowId}-${col}`] = el;
    else delete cellRefs.current[`${rowId}-${col}`];
  };

  /* ---- Cells (shared by desktop table + mobile cards) ---- */
  const componentCell = (r, locked, errs) => (
    <Box
      component="select"
      ref={setRef(r.id, "component")}
      value={r.component}
      disabled={locked}
      onChange={(e) => updateRow(r.id, "component", e.target.value)}
      onKeyDown={(e) => handleCellKeyDown(e, r, "component")}
      sx={{
        ...cellInputSx(errs.component, locked),
        cursor: locked ? "default" : "pointer",
        fontWeight: 600,
        color: NAVY,
      }}
    >
      {COMPONENTS.map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
    </Box>
  );

  const frameCell = (r, i, locked, errs) => (
    <Box sx={{ position: "relative" }}>
      <Box
        component="input"
        ref={setRef(r.id, "frameNo")}
        value={r.frameNo}
        disabled={locked}
        placeholder="Frame -24 / OCL 700"
        autoComplete="off"
        aria-label={`Row ${i + 1} Frame / OCL No`}
        onChange={(e) => {
          const input = e.target;
          const caret = input.selectionStart;
          const next = applyFrameNoTyping(r.frameNo, input.value);
          if (next === null) return; // only Frame.. / OCL.. can be typed
          updateRow(r.id, "frameNo", next);
          const expanded = next.length !== input.value.length; // p/s/c filled in a side
          requestAnimationFrame(() => {
            if (document.activeElement !== input) return;
            const pos = expanded ? next.length : caret;
            if (pos != null) input.setSelectionRange(pos, pos);
          });
        }}
        onFocus={() => setFrameFocusId(r.id)}
        onBlur={() => {
          setFrameFocusId((id) => (id === r.id ? null : id));
          // tidy on leaving the cell: "Frame12" -> "Frame 12"
          if (isValidFrameNo(r.frameNo) && normalizeFrameNo(r.frameNo) !== r.frameNo) {
            updateRow(r.id, "frameNo", normalizeFrameNo(r.frameNo));
          }
        }}
        onKeyDown={(e) => handleCellKeyDown(e, r, "frameNo")}
        sx={cellInputSx(errs.frameNo, locked)}
      />

      {/* "Frame -24" typed -> suggest the side (or type P / S / C) */}
      {/* Popper = shown above the table, not cut by the table scroll area */}
      <Popper
        open={!locked && frameFocusId === r.id && needsFrameSide(r.frameNo)}
        anchorEl={cellRefs.current[`${r.id}-frameNo`] || null}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            mt: 0.5,
            minWidth: 190,
            fontFamily: (t) => t.typography.fontFamily,
            backgroundColor: "#fff",
            border: "1px solid #d6dce5",
            borderRadius: "10px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
            py: 0.5,
          }}
        >
          {FRAME_SIDES.map((s) => (
            <Box
              key={s.key}
              role="option"
              aria-label={s.label}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the box
                updateRow(r.id, "frameNo", withFrameSide(r.frameNo, s.label));
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                cursor: "pointer",
                fontSize: "13px",
                color: NAVY,
                "&:hover": { backgroundColor: "#eef4fb" },
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "6px",
                  backgroundColor: "#e8f0fa",
                  fontSize: "11px",
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {s.key.toUpperCase()}
              </Box>
              {s.label}
            </Box>
          ))}
        </Box>
      </Popper>

      <ErrText>{errs.frameNo}</ErrText>
    </Box>
  );

  const dateCell = (r, i, col, locked, errs) => (
    <>
      <Box
        component="input"
        ref={setRef(r.id, col)}
        value={r[col]}
        disabled={locked}
        placeholder="yyyy-MM-dd"
        inputMode="numeric"
        maxLength={10}
        autoComplete="off"
        aria-label={`Row ${i + 1} ${col === "startDate" ? "Start Date" : "End Date"}`}
        onChange={(e) => updateRow(r.id, col, formatDateTyping(e.target.value))}
        onKeyDown={(e) => handleCellKeyDown(e, r, col)}
        sx={{ ...cellInputSx(errs[col], locked), fontVariantNumeric: "tabular-nums" }}
      />
      <ErrText>{errs[col]}</ErrText>
    </>
  );

  const weightCell = (r, i, locked, errs) => (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          component="input"
          ref={setRef(r.id, "weight")}
          value={r.weight}
          disabled={locked}
          placeholder="0.0"
          inputMode="decimal"
          autoComplete="off"
          aria-label={`Row ${i + 1} Weight`}
          onChange={(e) => updateRow(r.id, "weight", cleanWeight(e.target.value))}
          onKeyDown={(e) => handleCellKeyDown(e, r, "weight")}
          sx={{ ...cellInputSx(errs.weight, locked), pr: 4, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
        />
        <Box
          component="span"
          sx={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: "11px", color: "#9aa3b2", pointerEvents: "none" }}
        >
          kg
        </Box>
      </Box>
      <ErrText>{errs.weight}</ErrText>
    </>
  );

  const partsCell = (r, i, locked, errs) => {
    const partCount = splitParts(r.partNos).length;
    return (
      <>
        <Box sx={{ position: "relative" }}>
          <Box
            component="input"
            ref={setRef(r.id, "partNos")}
            value={r.partNos}
            disabled={locked}
            placeholder="265, 272, 275, 270"
            autoComplete="off"
            aria-label={`Row ${i + 1} Part Nos`}
            onChange={(e) => updateRow(r.id, "partNos", e.target.value)}
            onKeyDown={(e) => handleCellKeyDown(e, r, "partNos")}
            sx={{ ...cellInputSx(errs.partNos, locked), pr: 7 }}
          />
          {partCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "11px",
                fontWeight: 700,
                color: BLUE,
                backgroundColor: "#e8f0fa",
                borderRadius: "10px",
                px: 0.75,
                pointerEvents: "none",
              }}
            >
              {partCount} pcs
            </Box>
          )}
        </Box>
        <ErrText>{errs.partNos}</ErrText>
        {r.status === "error" && <ErrText>{r.message || "Failed to save"}</ErrText>}
      </>
    );
  };

  const statusCell = (r) => (
    <>
      {r.status === "saving" && <CircularProgress size={18} />}
      {r.status === "saved" && (
        <Tooltip title="Saved">
          <CheckCircleIcon sx={{ color: "#2e7d32", verticalAlign: "middle" }} />
        </Tooltip>
      )}
      {r.status === "error" && (
        <Tooltip title={r.message || "Failed"}>
          <ErrorOutlineIcon sx={{ color: "#c62828", verticalAlign: "middle" }} />
        </Tooltip>
      )}
      {(r.status === "" || r.status === "error") && (
        <Tooltip title="Remove row">
          <span>
            <IconButton size="small" tabIndex={-1} onClick={() => removeRow(r.id)} disabled={saving}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );

  const rowState = (r, i) => {
    const locked = r.status === "saved" || r.status === "saving" || saving;
    const errs = showErrors && !isRowEmpty(r) && r.status !== "saved" ? rowErrors(r) : {};
    const rowBg =
      r.status === "saved" ? "#f1f8f3" : r.status === "error" ? "#fff5f5" : i % 2 === 1 ? "#fbfcfe" : "#fff";
    return { locked, errs, rowBg };
  };

  const mobileLabel = (text) => (
    <Typography sx={{ fontSize: "11px", color: "#6b7280", fontWeight: 600, mb: 0.5, ml: 0.25 }}>{text}</Typography>
  );

  /* ---- Render ---- */
  const th = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: BLUE,
    color: "#fff",
    fontWeight: 600,
    fontSize: "13px",
    textAlign: "left",
    padding: "10px 8px",
    whiteSpace: "nowrap",
  };
  const td = { padding: "6px", borderBottom: "1px solid #eef1f5", verticalAlign: "top" };

  return (
    <>
      <Box>
      {/* ── IWO bar + actions ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: "16px",
          border: "1px solid #d6dce5",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) auto" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <Autocomplete
          options={iwoOptions}
          value={iwo}
          autoHighlight
          loading={iwoListLoading}
          disabled={saving}
          onChange={(_, val) => {
            setIwo(val);
            if (val) {
              const first = rows.find((r) => r.status !== "saved");
              if (first) setTimeout(() => focusCell(first.id, "component"), 0);
            }
          }}
          getOptionLabel={(o) => (o ? `${o.IwoNo} - ${o.EngSpec || ""}`.trim() : "")}
          isOptionEqualToValue={(o, v) => String(o.IwoNo) === String(v.IwoNo)}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter((o) =>
              [o.IwoNo, o.EngSpec, o.AdditionalDescription].some((v) => String(v || "").toLowerCase().includes(q))
            );
          }}
          renderOption={(props, o) => (
            <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66" }}>
                {o.IwoNo} <Box component="span" sx={{ fontWeight: 400, color: "#333" }}>- {o.EngSpec}</Box>
              </Typography>
              {o.AdditionalDescription && (
                <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>{o.AdditionalDescription}</Typography>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={iwoRef}
              label="IWO No - Eng Spec"
              placeholder="Search IWO no or spec"
              error={!!iwoListError}
              helperText={
                iwoListError ||
                (iwo ? `${iwo.AdditionalDescription || ""}  ·  ${iwo.StartDate} → ${iwo.EndDate}` : " ")
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {iwoListLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", "& > *": { flex: { xs: 1, md: "none" } } }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleNewBatch}
            disabled={saving}
            tabIndex={-1}
            sx={{ height: { xs: 48, md: 56 }, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
          >
            New batch
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={requestSave}
            disabled={saving}
            tabIndex={-1}
            sx={{
              height: { xs: 48, md: 56 },
              px: 3,
              borderRadius: "12px",
              backgroundColor: "#2e7d32",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
          >
            Save {readyRows.length > 0 ? `(${readyRows.length})` : ""}
          </Button>
        </Box>
      </Paper>

      {/* ── Mobile: one card per row ── */}
      {isMobile && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {rows.map((r, i) => {
            const { locked, errs, rowBg } = rowState(r, i);
            return (
              <Paper
                key={r.id}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "14px",
                  border: "1px solid",
                  borderColor: r.status === "error" ? "#e57373" : r.status === "saved" ? "#a8d5b5" : "#d6dce5",
                  backgroundColor: rowBg === "#fbfcfe" ? "#fff" : rowBg,
                  "&:focus-within": { borderColor: r.status === "saved" ? "#a8d5b5" : BLUE },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0E3C66" }}>Frame #{i + 1}</Typography>
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>{statusCell(r)}</Box>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                  <Box>
                    {mobileLabel("Component")}
                    {componentCell(r, locked, errs)}
                  </Box>
                  <Box>
                    {mobileLabel("Frame / OCL No")}
                    {frameCell(r, i, locked, errs)}
                  </Box>
                  <Box>
                    {mobileLabel("Start Date")}
                    {dateCell(r, i, "startDate", locked, errs)}
                  </Box>
                  <Box>
                    {mobileLabel("End Date")}
                    {dateCell(r, i, "endDate", locked, errs)}
                  </Box>
                  <Box>
                    {mobileLabel("Weight (kg)")}
                    {weightCell(r, i, locked, errs)}
                  </Box>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    {mobileLabel("Part Nos")}
                    {partsCell(r, i, locked, errs)}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Desktop: table ── */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #d6dce5",
            overflow: "auto",
            maxHeight: "60vh",
            boxShadow: "0px 6px 15px rgba(0,0,0,0.06)",
          }}
        >
          <Box component="table" sx={{ width: "100%", minWidth: 900, borderCollapse: "separate", borderSpacing: 0 }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={{ ...th, width: 44, textAlign: "center" }}>#</Box>
                <Box component="th" sx={{ ...th, width: 150 }}>Component</Box>
                <Box component="th" sx={{ ...th, width: 230 }}>Frame / OCL No</Box>
                <Box component="th" sx={{ ...th, width: 140 }}>Start Date</Box>
                <Box component="th" sx={{ ...th, width: 140 }}>End Date</Box>
                <Box component="th" sx={{ ...th, width: 120 }}>Weight (kg)</Box>
                <Box component="th" sx={th}>Part Nos</Box>
                <Box component="th" sx={{ ...th, width: 90, textAlign: "center" }}>Status</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r, i) => {
                const { locked, errs, rowBg } = rowState(r, i);
                return (
                  <Box
                    component="tr"
                    key={r.id}
                    sx={{ backgroundColor: rowBg, "&:focus-within": { backgroundColor: r.status === "saved" ? rowBg : "#eef4fb" } }}
                  >
                    <Box component="td" sx={{ ...td, textAlign: "center", color: "#9aa3b2", fontSize: "12px", pt: "15px" }}>
                      {i + 1}
                    </Box>
                    <Box component="td" sx={td}>{componentCell(r, locked, errs)}</Box>
                    <Box component="td" sx={td}>{frameCell(r, i, locked, errs)}</Box>
                    <Box component="td" sx={td}>{dateCell(r, i, "startDate", locked, errs)}</Box>
                    <Box component="td" sx={td}>{dateCell(r, i, "endDate", locked, errs)}</Box>
                    <Box component="td" sx={td}>{weightCell(r, i, locked, errs)}</Box>
                    <Box component="td" sx={td}>{partsCell(r, i, locked, errs)}</Box>
                    <Box component="td" sx={{ ...td, textAlign: "center", pt: "10px", whiteSpace: "nowrap" }}>
                      {statusCell(r)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── Footer ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1.5,
          flexWrap: "wrap",
          gap: 1,
          // phones: keep Add row + counts visible at the bottom
          position: { xs: "sticky", md: "static" },
          bottom: 0,
          zIndex: 5,
          py: { xs: 1, md: 0 },
          px: { xs: 0.5, md: 0 },
          backgroundColor: { xs: "rgba(245,247,251,0.96)", md: "transparent" },
        }}
      >
        <Button
          startIcon={<AddIcon />}
          tabIndex={-1}
          disabled={saving}
          onClick={() => addRowAfter(rows[rows.length - 1]?.id)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Add row
        </Button>
        <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>
          Ready: <b style={{ color: "#2e7d32" }}>{readyRows.length}</b>
          {invalidRows.length > 0 && (
            <>
              {"  ·  "}Incomplete: <b style={{ color: "#c62828" }}>{invalidRows.length}</b>
            </>
          )}
          {savedCount > 0 && (
            <>
              {"  ·  "}Saved: <b style={{ color: BLUE }}>{savedCount}</b>
            </>
          )}
          {"  ·  "}Created by <b>{createdBy || "—"}</b>
        </Typography>
        {isMobile && (
          <Button
            fullWidth
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={requestSave}
            disabled={saving}
            sx={{
              height: 48,
              borderRadius: "12px",
              backgroundColor: "#2e7d32",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
          >
            Save {readyRows.length > 0 ? `(${readyRows.length})` : ""}
          </Button>
        )}
      </Box>

      </Box>

      {/* ── Inserted frames (GetFrameDetails) ── */}
      <InsertedFrames iwo={iwo} isMobile={isMobile} />

      {/* Confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: 3, minWidth: { xs: "auto", sm: 380 }, mx: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#1e3c72" }}>Save frames?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Save <b>{readyRows.length}</b> frame(s) for IWO <b>{iwo?.IwoNo}</b>
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>{iwo?.EngSpec}</Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            autoFocus
            onClick={handleSave}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" } }}
          >
            Yes, Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
};

/* -------------------- Page -------------------- */
const InsertFrames = () => (
  <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
    <HeaderBannerPage />

    <Box
      sx={{
        maxWidth: "1400px",
        width: "100%",
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: 3,
        boxSizing: "border-box",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#0E3C66", mb: 2, letterSpacing: "0.5px" }}>
        Insert Frames
      </Typography>

      <InsertFramesTable />
    </Box>
  </Box>
);

export default InsertFrames;